import { useCallback, useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router';
// material
import { Box, Button, Container, Stack, Typography } from '@mui/material';
import { experimentalStyled as styled } from '@mui/material/styles';
// routes
import { PATH_AUTH } from 'src/routes/paths';
// components
import Page from 'src/components/Page';
import { ResetPasswordForm } from 'src/components/authentication/reset-password';
//
import { SentIcon } from 'src/assets/index';
import Logo from 'src/components/Logo';
import CopyrightMessage from 'src/components/authentication/login/V2/CopyrightMessage';

// ----------------------------------------------------------------------

const RootStyle = styled(Page)(() => ({
  display: 'flex',
  minHeight: '100%',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
}));

const HeaderStyle = styled('header')(({ theme }) => ({
  top: 0,
  left: 0,
  lineHeight: 0,
  width: '100%',
  position: 'absolute',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(5, 5, 0),
  },
}));

export default function ResetPassword({
  userEmail: propUserEmail = '',
  onBack,
  showLogo = true,
  showCopyright = true,
  extendSession = false,
  /** When true, render form only (no Page/Container) for embedding in another layout */
  embedded = false,
}) {
  const { state: locationState } = useLocation();
  const userEmail = propUserEmail || locationState?.email || '';
  const [email, setEmail] = useState(userEmail);
  const [sent, setSent] = useState(false);

  const navigate = useNavigate();

  const handleBack = useCallback(() => {
    if (onBack && typeof onBack === 'function') {
      onBack();
      return;
    }
    navigate(PATH_AUTH.login);
  }, [navigate, onBack]);

  const formContent = !sent ? (
    <Stack spacing={3}>
      <Typography variant="h5" fontWeight={600}>
        Forgot your password?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Please enter the email address associated with your account and we will
        email you a link to reset your password.
      </Typography>

      <ResetPasswordForm
        email={userEmail}
        onSent={() => setSent(true)}
        onGetEmail={(value) => setEmail(value)}
        extendSession={extendSession}
      />

      <Button
        variant="text"
        size="medium"
        onClick={handleBack}
        sx={{
          alignSelf: 'flex-start',
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
            backgroundColor: 'transparent',
          },
        }}
      >
        Back
      </Button>
    </Stack>
  ) : (
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="h3" gutterBottom>
        Request sent successfully.
      </Typography>
      <SentIcon sx={{ mb: 5, mx: 'auto', height: 160, mt: 5 }} />
      <Typography
        variant="body1"
        gutterBottom
        sx={{
          fontSize: '16px',
        }}
      >
        An email will be sent to <strong>{email}</strong> if it is found in our
        system.
      </Typography>
      <Typography variant="body1" gutterBottom>
        Please check your inbox and spam folder.
      </Typography>

      <Button
        size="large"
        variant="contained"
        onClick={handleBack}
        sx={{ mt: 5 }}
      >
        Back
      </Button>
    </Box>
  );

  if (embedded) {
    return <Box sx={{ maxWidth: 480 }}>{formContent}</Box>;
  }

  return (
    <RootStyle title="Pronto Housing | Reset Password">
      {showLogo && (
        <HeaderStyle>
          <RouterLink to="/">
            <Logo />
          </RouterLink>
        </HeaderStyle>
      )}
      <Container>
        <Box sx={{ maxWidth: 480, mx: 'auto' }}>{formContent}</Box>
      </Container>
      {showCopyright && <CopyrightMessage />}
    </RootStyle>
  );
}
